import { Fragment, type ReactNode, useCallback } from 'react';
import { HStack, type StackProps } from '@direct-frontend/components';
import { type OpenApiSchemaVariantExternalID } from '#shared/api/schema';
import { type CharacteristicPartial, getGroupedCharacteristicsLine } from '#shared/lib/characteristics';
import { INTEGRATION_EXTERNAL_SYSTEM_ICONS } from '#shared/lib/const';
import { DELIMITER_DOT } from '#shared/lib/constants';
import { copyTextToClipboard } from '#shared/lib/string';
import styles from './HeaderCaption.module.css';

const getSellerSkuCaption = (sellerSku?: string) => {
    if (!sellerSku) {
        return '';
    }

    return `${sellerSku}`;
};

const getKitIdCaption = (kitId?: number) => {
    if (!kitId) {
        return '';
    }

    return `KIT ID ${kitId}`;
};

const useExternalSystemsCaption = (externalSystems: OpenApiSchemaVariantExternalID[]) => {
    const externalSystemItems = (externalSystems || []).reduce<
        { icon: ReactNode; externalId: string; system_type: OpenApiSchemaVariantExternalID['system_type'] }[]
    >((acc, { system_type, external_id }) => {
        const icon = INTEGRATION_EXTERNAL_SYSTEM_ICONS.find((icon) => icon.system_type === system_type);

        if (icon) {
            acc.push({ icon: icon.icon, externalId: external_id, system_type });
        }

        return acc;
    }, []);

    const handleCopy = useCallback((externalId?: string) => {
        copyTextToClipboard(externalId);
    }, []);

    if (!externalSystemItems.length) {
        return null;
    }

    return (
        <HStack alignItems="center">
            {externalSystemItems.map(({ icon, externalId, system_type }, index) => (
                <Fragment key={system_type}>
                    {index > 0 ? DELIMITER_DOT : null}
                    <span className={styles.externalSystemIcon} onClick={() => handleCopy(externalId)}>
                        {icon}
                    </span>
                </Fragment>
            ))}
        </HStack>
    );
};

export const HeaderCaption = ({
    sellerSku,
    kitId,
    characteristics,
    externalSystems,
    alignItems,
}: {
    sellerSku?: string;
    kitId?: number;
    characteristics: CharacteristicPartial[];
    externalSystems: OpenApiSchemaVariantExternalID[];
    alignItems?: StackProps['alignItems'];
}) => {
    const sellerSkuCaption = getSellerSkuCaption(sellerSku);
    const kitIdCaption = getKitIdCaption(kitId);
    const characteristicsCaption = getGroupedCharacteristicsLine(undefined, characteristics);
    const externalSystemsCaption = useExternalSystemsCaption(externalSystems);

    const captionParts: string[] = [sellerSkuCaption, kitIdCaption, characteristicsCaption].filter(Boolean);
    const captionString = captionParts.join(DELIMITER_DOT);

    return (
        <HStack alignItems={alignItems}>
            {captionString}
            {externalSystemsCaption ? (
                <Fragment>
                    {captionParts.length > 0 ? DELIMITER_DOT : null}
                    {externalSystemsCaption}
                </Fragment>
            ) : null}
        </HStack>
    );
};
